import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router';
import { useUserProfile } from '@/contexts/user.context';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useStore } from 'jotai';
import { selectedSettingsAccountTabAtom } from '@/store';
import { useTabPersistence } from '@/hooks/useTabPersistence';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { CancelAccount } from '@/components/settings/account/cancel-account';
import { UserCircle, LogOut, ExternalLink } from 'lucide-react';
import { EmailPreferences } from '@/components/settings/account/email-preferences';
import { PasswordCard } from '@/components/settings/privacy/password-change';
import { TabCardPanel } from '@/components/glitch-ui/tab-card-panel';
import { PageShell } from '@/components/glitch-ui/page-shell';
import { IntegrationSettings } from '@/components/settings/integrations';
import { ApiKeysSettings, ConnectedAppsSettings } from '@/components/settings/api-keys';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { clearAllUserData } from '@/lib/auth-cleanup';
import { defaultProfile, deleteAccessTokenCookie } from '@/lib/auth';
import { userProfileQueryOptions } from '@/lib/query-options';

const GOOGLE_ACCOUNT_SECURITY_URL = 'https://myaccount.google.com/security';

export const Route = createFileRoute('/_authenticated/_layout/settings/_layout/account')({
  component: Account,
  staticData: {
    title: 'Account',
    icon: UserCircle,
  },
});

const TAB_IDS = ['account', 'integrations', 'api-keys'] as const;

function Account() {
  const { user, setUser } = useUserProfile();
  // Fresh /user/me load — needed for has_password, which the JWT-derived
  // useUserProfile() doesn't carry. _authenticated.tsx already primes this
  // query, so this is a cache read in practice.
  const { data: freshProfile } = useSuspenseQuery(userProfileQueryOptions());
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const store = useStore();

  const logout = () => {
    deleteAccessTokenCookie();
    setUser(defaultProfile);
    clearAllUserData(queryClient, store);
    navigate({ to: '/signin' });
  };

  const { activeTab, handleTabChange } = useTabPersistence(
    selectedSettingsAccountTabAtom,
    TAB_IDS,
    'account',
    (tabId, replace) => navigate({ to: '/settings/account', hash: tabId, replace }),
    location.hash,
  );

  return (
    <PageShell toolbar={<div className="flex items-center gap-3"></div>}>
      <TabCardPanel
          tabs={[
            { id: 'account', label: 'Account', isActive: activeTab === 'account', onClick: () => handleTabChange('account') },
            { id: 'integrations', label: 'Integrations', isActive: activeTab === 'integrations', onClick: () => handleTabChange('integrations') },
            { id: 'api-keys', label: 'API Keys', isActive: activeTab === 'api-keys', onClick: () => handleTabChange('api-keys') },
          ]}
        >
          {/* Account Tab */}
          {activeTab === 'account' && (
            <div className="space-y-8">
              {/* Account Details Section */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Details</h2>
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback className="text-lg">
                      {user.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="space-y-1">
                    <h3 className="text-lg font-medium text-gray-900">{user.name || 'N/A'}</h3>
                    <p className="text-gray-700">{user.email || 'N/A'}</p>
                  </div>
                </div>

                {user.impersonating_email && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm font-medium text-yellow-800">Impersonating</p>
                    <p className="text-sm text-yellow-700">{user.impersonating_email}</p>
                  </div>
                )}
              </div>

              {/* Email Preferences Section */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Email Preferences</h2>
                <EmailPreferences />
              </div>

              {/* Password Section */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Password</h2>
                {freshProfile.has_password ? (
                  <PasswordCard />
                ) : (
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-gray-900 font-medium">Sign-in managed by Google</h3>
                          <p className="text-gray-700 text-sm">
                            Your account uses Google sign-in. Manage your password on Google.
                          </p>
                        </div>
                        <Button variant="outline" asChild>
                          <a
                            href={GOOGLE_ACCOUNT_SECURITY_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Manage Google account
                            <ExternalLink className="h-4 w-4 ml-2" />
                          </a>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Account Management Section */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Management</h2>
                <div className="space-y-4">
                  <Card className="rounded-lg border bg-white">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-gray-900 font-medium">Sign out</h3>
                          <p className="text-gray-700 text-sm">Sign out of your account on this device.</p>
                        </div>
                        <Button
                          variant="outline"
                          onClick={logout}
                          className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                        >
                          <LogOut className="h-4 w-4 mr-2" />
                          Sign out
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  <CancelAccount />
                </div>
              </div>
            </div>
          )}

          {/* Integrations Tab */}
          {activeTab === 'integrations' && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Integrations</h2>
              <IntegrationSettings />
            </div>
          )}

          {/* API Keys Tab */}
          {activeTab === 'api-keys' && (
            <>
              <ApiKeysSettings />
              <ConnectedAppsSettings />
            </>
          )}

        </TabCardPanel>
    </PageShell>
  );
}
